import React, { useState, useEffect } from 'react';
import { Table, Card, Input, Select, DatePicker, Button, Tag, Space, Modal, Descriptions, Row, Col, Statistic, message, Form, InputNumber } from 'antd';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import api from '../../utils/api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;

const ThirdPartyOrders = () => {
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [currencyStats, setCurrencyStats] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [filters, setFilters] = useState({});
  const [apps, setApps] = useState([]);
  const [detailVisible, setDetailVisible] = useState(false);
  const [orderDetail, setOrderDetail] = useState(null);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [confirmOrder, setConfirmOrder] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmForm] = Form.useForm();

  useEffect(() => {
    fetchApps();
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [page, filters]);

  const fetchApps = async () => {
    try {
      const response = await api.get('/admin/third-party/apps', { params: { status: 1, limit: 100 } });
      setApps(response.data.list || []);
    } catch (error) {
      console.error('获取应用列表失败', error);
    }
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        ...filters
      };
      if (filters.dateRange) {
        params.startDate = filters.dateRange[0].format('YYYY-MM-DD');
        params.endDate = filters.dateRange[1].format('YYYY-MM-DD');
        delete params.dateRange;
      }
      const response = await api.get('/admin/third-party/orders', { params });
      setOrders(response.data.list || []);
      setTotal(response.data.pagination?.total || 0);
      setTotalAmount(response.data.total_amount || 0);

      const statParams = { ...params };
      delete statParams.page;
      delete statParams.limit;
      const statResponse = await api.get('/admin/third-party/orders/statistics', { params: statParams });
      setCurrencyStats(statResponse.data.currency_stats || []);
    } catch (error) {
      message.error('获取订单列表失败');
    }
    setLoading(false);
  };

  const handleSearch = (values) => {
    setFilters(values);
    setPage(1);
  };

  const handleReset = () => {
    setFilters({});
    setPage(1);
  };

  const handleViewDetail = async (record) => {
    try {
      const response = await api.get(`/admin/third-party/orders/${record.trade_no}`);
      setOrderDetail(response.data);
      setDetailVisible(true);
    } catch (error) {
      message.error('获取详情失败');
    }
  };

  const handleNotify = async (tradeNo) => {
    try {
      await api.post(`/admin/third-party/orders/${tradeNo}/notify`);
      message.success('通知已发送');
    } catch (error) {
      message.error('通知发送失败');
    }
  };

  const handleConfirmPay = async (record) => {
    try {
      const response = await api.get(`/admin/third-party/orders/${record.trade_no}`);
      setConfirmOrder(response.data);
      setConfirmVisible(true);
      confirmForm.setFieldsValue({ actual_amount: parseFloat(response.data.total_amount) });
    } catch (error) {
      message.error('获取订单详情失败');
    }
  };

  const handleConfirmPaySubmit = async () => {
    try {
      const values = await confirmForm.validateFields();
      setConfirmLoading(true);
      await api.post(`/admin/third-party/orders/${confirmOrder.trade_no}/confirm`, {
        actual_amount: values.actual_amount
      });
      message.success('已确认支付');
      setConfirmVisible(false);
      setConfirmOrder(null);
      confirmForm.resetFields();
      fetchOrders();
    } catch (error) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      }
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleRejectPay = async (tradeNo) => {
    try {
      const reason = prompt('请输入拒绝原因:');
      if (reason === null) return;
      await api.post(`/admin/third-party/orders/${tradeNo}/reject`, { reason });
      message.success('已拒绝');
      fetchOrders();
    } catch (error) {
      message.error(error.response?.data?.message || '操作失败');
    }
  };

  const getStatusTag = (status) => {
    const statusMap = {
      pending: { color: 'processing', text: '待支付' },
      processing: { color: 'warning', text: '处理中' },
      paid: { color: 'success', text: '已支付' },
      failed: { color: 'error', text: '失败' },
      expired: { color: 'default', text: '已过期' },
      closed: { color: 'default', text: '已关闭' }
    };
    const config = statusMap[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const columns = [
    {
      title: '订单号',
      dataIndex: 'trade_no',
      key: 'trade_no',
      width: 200
    },
    // {
    //   title: '商户订单号',
    //   dataIndex: 'out_trade_no',
    //   key: 'out_trade_no',
    //   width: 200
    // },
    {
      title: '应用名称',
      dataIndex: 'app_name',
      key: 'app_name',
      width: 100
    },
    {
      title: '商品标题',
      dataIndex: 'subject',
      key: 'subject',
      ellipsis: true,
       width: 200
    },
    {
      title: '金额',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (val, record) => {
        const currencyCode = record.currency_code || 'CNY';
        const symbols = { 'CNY': '¥', 'USD': '$', 'EUR': '€', 'GBP': '£', 'KES': 'KSh' };
        const symbol = symbols[currencyCode] || currencyCode;
        return `${symbol}${val} ${currencyCode}`;
      },
      width: 260
    },
    {
      title: '支付方式',
      dataIndex: 'payment_method',
      key: 'payment_method',
      render: (method) => {
        const names = {
          'bank_transfer': '银行转账',
          'alipay': '支付宝',
          'wechat': '微信',
          'usdt': 'USDT',
          'm-pesa': 'M-Pesa',
          'mobile_money': 'Mobile Money'
        };
        return names[method] || method;
      },
      width: 160
    },
    {
      title: '付款人',
      dataIndex: 'payer_info',
      key: 'payer_info',
      render: (info) => {
        if (!info) return '-';
        if (typeof info === 'string') {
          try { info = JSON.parse(info); } catch (e) { return info; }
        }
        const keys = Object.keys(info);
        if (keys.length === 0) return '-';
        return keys.slice(0, 2).map(k => `${k}: ${info[k]}`).join(', ') + (keys.length > 2 ? '...' : '');
      }
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => getStatusTag(status),
      width: 100
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => handleViewDetail(record)}>
            详情
          </Button>
          {(record.status === 'processing' || record.status === 'pending') && (
            <>
              <Button type="link" size="small" onClick={() => handleConfirmPay(record)} style={{ color: '#52c41a' }}>
                确认支付
              </Button>
              <Button type="link" size="small" onClick={() => handleRejectPay(record.trade_no)} style={{ color: '#ff4d4f' }}>
                拒绝
              </Button>
            </>
          )}
          {record.status === 'paid' && (
            <Button type="link" size="small" onClick={() => handleNotify(record.trade_no)}>
              通知
            </Button>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Space wrap>
            <Select
              placeholder="选择应用"
              allowClear
              style={{ width: 150 }}
              onChange={(val) => setFilters({ ...filters, app_id: val })}
              value={filters.app_id}
            >
              {apps.map(app => (
                <Option key={app.app_id} value={app.app_id}>{app.app_name}</Option>
              ))}
            </Select>
            <Select
              placeholder="订单状态"
              allowClear
              style={{ width: 120 }}
              onChange={(val) => setFilters({ ...filters, status: val })}
              value={filters.status}
            >
              <Option value="pending">待支付</Option>
              <Option value="processing">处理中</Option>
              <Option value="paid">已支付</Option>
              <Option value="failed">失败</Option>
              <Option value="expired">已过期</Option>
              <Option value="closed">已关闭</Option>
            </Select>
            <Select
              placeholder="支付方式"
              allowClear
              style={{ width: 120 }}
              onChange={(val) => setFilters({ ...filters, payment_method: val })}
              value={filters.payment_method}
            >
              <Option value="bank_transfer">银行转账</Option>
              <Option value="alipay">支付宝</Option>
              <Option value="wechat">微信</Option>
              <Option value="usdt">USDT</Option>
              <Option value="m-pesa">M-Pesa</Option>
              <Option value="mobile_money">Mobile Money</Option>
            </Select>
            <RangePicker 
              onChange={(dates) => setFilters({ ...filters, dateRange: dates })} 
              value={filters.dateRange}
            />
            <Input 
              placeholder="订单号/商户订单号" 
              style={{ width: 200 }}
              onChange={(e) => setFilters({ ...filters, keyword: e.target.value })}
              value={filters.keyword}
            />
            <Button type="primary" icon={<SearchOutlined />} onClick={() => fetchOrders()}>
              搜索
            </Button>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              重置
            </Button>
          </Space>
        </div>

        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={4}>
            <Statistic title="订单总数" value={total} />
          </Col>
          {currencyStats.map((item, index) => {
            const symbols = { 'CNY': '¥', 'USD': '$', 'EUR': '€', 'GBP': '£', 'KES': 'KSh' };
            const symbol = symbols[item.currency_code] || item.currency_code;
            return (
              <Col span={4} key={item.currency_code || index}>
                <Statistic 
                  title={`订单金额 (${item.currency_code})`} 
                  value={item.paid_amount || 0} 
                  prefix={symbol} 
                  precision={2} 
                />
              </Col>
            );
          })}
        </Row>

        <Table
          columns={columns}
          dataSource={orders}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            pageSize: limit,
            total: total,
            onChange: setPage,
            showSizeChanger: false
          }}
        />
      </Card>

      <Modal
        title="订单详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={700}
      >
        {orderDetail && (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="平台订单号" span={2}>{orderDetail.trade_no}</Descriptions.Item>
            <Descriptions.Item label="商户订单号" span={2}>{orderDetail.out_trade_no}</Descriptions.Item>
            <Descriptions.Item label="应用名称">{orderDetail.app_name}</Descriptions.Item>
            <Descriptions.Item label="状态">{getStatusTag(orderDetail.status)}</Descriptions.Item>
            <Descriptions.Item label="商品标题" span={2}>{orderDetail.subject}</Descriptions.Item>
            <Descriptions.Item label="货币代码">{orderDetail.currency_code || 'CNY'}</Descriptions.Item>
            <Descriptions.Item label="订单金额">
              {(() => {
                const currencyCode = orderDetail.currency_code || 'CNY';
                const symbols = { 'CNY': '¥', 'USD': '$', 'EUR': '€', 'GBP': '£', 'KES': 'KSh' };
                const symbol = symbols[currencyCode] || currencyCode;
                return `${symbol}${orderDetail.total_amount} ${currencyCode}`;
              })()}
            </Descriptions.Item>
            <Descriptions.Item label="实际金额">
              {orderDetail.actual_amount ? (() => {
                const currencyCode = orderDetail.currency_code || 'CNY';
                const symbols = { 'CNY': '¥', 'USD': '$', 'EUR': '€', 'GBP': '£', 'KES': 'KSh' };
                const symbol = symbols[currencyCode] || currencyCode;
                return `${symbol}${orderDetail.actual_amount} ${currencyCode}`;
              })() : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="支付方式">
              {orderDetail.payment_method === 'bank_transfer' && 'Bank Transfer'}
              {orderDetail.payment_method === 'alipay' && 'Alipay'}
              {orderDetail.payment_method === 'wechat' && 'WeChat Pay'}
              {orderDetail.payment_method === 'usdt' && 'USDT'}
              {orderDetail.payment_method === 'm-pesa' && 'M-Pesa'}
              {orderDetail.payment_method === 'mobile_money' && 'Mobile Money'}
            </Descriptions.Item>
            <Descriptions.Item label="第三方交易号">{orderDetail.transaction_id || '-'}</Descriptions.Item>
            <Descriptions.Item label="支付凭证" span={2}>
              {orderDetail.payment_info ? (
                typeof orderDetail.payment_info === 'string' 
                  ? orderDetail.payment_info 
                  : JSON.stringify(orderDetail.payment_info)
              ) : '-'}
            </Descriptions.Item>
            {orderDetail.account_info && (
              <>
                <Descriptions.Item label="收款账户" span={2}>
                  {orderDetail.payment_method === 'bank_transfer' && orderDetail.account_info.bank_name && 
                    `${orderDetail.account_info.bank_name} - ${orderDetail.account_info.account_name} (${orderDetail.account_info.bank_account})`}
                  {orderDetail.payment_method === 'alipay' && orderDetail.account_info.alipay_account && 
                    `${orderDetail.account_info.alipay_account} - ${orderDetail.account_info.account_name}`}
                  {orderDetail.payment_method === 'wechat' && orderDetail.account_info.wechat_account && 
                    `${orderDetail.account_info.wechat_account}`}
                  {orderDetail.payment_method === 'usdt' && orderDetail.account_info.usdt_address && 
                    `${orderDetail.account_info.usdt_address} (${orderDetail.account_info.usdt_network})`}
                  {orderDetail.payment_method === 'm-pesa' && orderDetail.account_info.mpesa_phone && 
                    `${orderDetail.account_info.mpesa_phone} - ${orderDetail.account_info.mpesa_name}`}
                  {orderDetail.payment_method === 'mobile_money' && orderDetail.account_info.mpesa_phone && 
                    `${orderDetail.account_info.mpesa_phone} - ${orderDetail.account_info.mpesa_name}`}
                </Descriptions.Item>
              </>
            )}
            {orderDetail.payer_info && (
              <Descriptions.Item label="付款人信息" span={2}>
                {typeof orderDetail.payer_info === 'object' 
                  ? Object.entries(orderDetail.payer_info).map(([key, val]) => (
                      <div key={key}><span style={{ color: '#666' }}>{key}:</span> {val}</div>
                    ))
                  : orderDetail.payer_info}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="创建时间">{orderDetail.created_at ? dayjs(orderDetail.created_at).format('YYYY-MM-DD HH:mm:ss') : '-'}</Descriptions.Item>
            <Descriptions.Item label="支付时间">{orderDetail.payment_time ? dayjs(orderDetail.payment_time).format('YYYY-MM-DD HH:mm:ss') : '-'}</Descriptions.Item>
            <Descriptions.Item label="过期时间">{orderDetail.expire_time ? dayjs(orderDetail.expire_time).format('YYYY-MM-DD HH:mm:ss') : '-'}</Descriptions.Item>
            <Descriptions.Item label="回调地址" span={2}>
              {orderDetail.callback_url ? (
                <a href={orderDetail.callback_url} target="_blank" rel="noopener noreferrer">{orderDetail.callback_url}</a>
              ) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="异步通知地址" span={2}>
              {orderDetail.notify_url ? (
                <a href={orderDetail.notify_url} target="_blank" rel="noopener noreferrer">{orderDetail.notify_url}</a>
              ) : '-'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      <Modal
        title="确认支付"
        open={confirmVisible}
        onOk={handleConfirmPaySubmit}
        onCancel={() => {
          setConfirmVisible(false);
          setConfirmOrder(null);
          confirmForm.resetFields();
        }}
        confirmLoading={confirmLoading}
        okText="确认支付"
        cancelText="取消"
        width={600}
      >
        {confirmOrder && (
          <>
            <Descriptions column={2} bordered size="small" style={{ marginBottom: 24 }}>
              <Descriptions.Item label="订单号" span={2}>{confirmOrder.trade_no}</Descriptions.Item>
              <Descriptions.Item label="应用名称">{confirmOrder.app_name}</Descriptions.Item>
              <Descriptions.Item label="支付方式">
                {(() => {
                  const names = {
                    'bank_transfer': '银行转账',
                    'alipay': '支付宝',
                    'wechat': '微信',
                    'usdt': 'USDT',
                    'm-pesa': 'M-Pesa',
                    'mobile_money': 'Mobile Money'
                  };
                  return names[confirmOrder.payment_method] || confirmOrder.payment_method;
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="商品标题" span={2}>{confirmOrder.subject}</Descriptions.Item>
              {confirmOrder.payer_info && (
                <Descriptions.Item label="付款人信息" span={2}>
                  {typeof confirmOrder.payer_info === 'object'
                    ? Object.entries(confirmOrder.payer_info).map(([key, val]) => (
                        <div key={key}><span style={{ color: '#666' }}>{key}:</span> {val}</div>
                      ))
                    : confirmOrder.payer_info}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="原始金额" span={2}>
                <span style={{ fontSize: 16, fontWeight: 'bold' }}>
                  {(() => {
                    const currencyCode = confirmOrder.currency_code || 'CNY';
                    const symbols = { 'CNY': '¥', 'USD': '$', 'EUR': '€', 'GBP': '£', 'KES': 'KSh' };
                    const symbol = symbols[currencyCode] || currencyCode;
                    return `${symbol}${confirmOrder.total_amount} ${currencyCode}`;
                  })()}
                </span>
              </Descriptions.Item>
            </Descriptions>

            <Form
              form={confirmForm}
              layout="vertical"
              onFinish={handleConfirmPaySubmit}
            >
              <Form.Item
                label="实际支付金额"
                name="actual_amount"
                rules={[
                  { required: true, message: '请输入实际支付金额' },
                  { type: 'number', min: 0.01, message: '金额必须大于0' }
                ]}
                extra="请根据实际到账金额填写，将用于结算"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0.01}
                  step={0.01}
                  precision={2}
                  placeholder="请输入实际支付金额"
                />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>
    </div>
  );
};

export default ThirdPartyOrders;
